import React, { useRef, useEffect, useCallback, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import debounce from 'lodash/debounce';
import { validateAndAdjustOfferValue } from '../../../../utils/offerThresholdUtils';

// react-number-format forwards these to customInput; MUI TextField passes unknown keys to the DOM.
const FORMAT_PROPS = new Set([
  'thousandSeparator',
  'decimalSeparator',
  'decimalScale',
  'fixedDecimalScale',
  'allowNegative',
  'allowLeadingZeros',
  'suffix',
  'prefix',
  'isNumericString',
  'valueIsNumericString',
  'format',
  'removeFormatting',
  'mask',
  'isAllowed',
  'getCaretBoundary',
  'isValidInputCharacter',
  'isCharacterSame',
]);

const createCustomInputAdapter = (InputComponent) => {
  const Adapter = React.forwardRef((props, ref) => {
    const muiProps = {};
    for (const [key, val] of Object.entries(props)) {
      if (!FORMAT_PROPS.has(key)) muiProps[key] = val;
    }
    return <InputComponent {...muiProps} inputRef={ref} />;
  });
  Adapter.displayName = 'NumericFormatCustomInputAdapter';
  return Adapter;
};

/**
 * Generalized NumericInputWithValidation component
 * Provides debounced validation for numeric inputs with IFMV/AV limits and shows toasts on adjustment.
 *
 * @param {Object} props
 * @param {string|number} props.value - Current input value
 * @param {Function} props.onValueChange - Callback when value changes
 * @param {Object} props.row - Row/case data for IFMV and assessment
 * @param {boolean} props.isAVMode - Whether input is in AV mode (vs market value mode)
 * @param {number} props.debounceDelay - Debounce delay in ms (default: 2000)
 * @param {boolean} props.enableValidation - Whether to enable validation (default: false; set true for grid offer cells)
 * @param {React.Component} props.customInput - Custom input component (default: Input from MUI)
 */

const NumericInputWithValidation = ({
  value,
  onValueChange,
  row,
  isAVMode = false,
  debounceDelay = 2000,
  enableValidation = false,
  customInput,
  inputProps = {},
  ...otherProps
}) => {
  
  // Use the value as-is - no PropertyAssessment default
  // Defaults are handled in the parent component (OfferEditCell)
  const displayValue = useMemo(() => {
    // If value is provided and not empty, always use it as-is
    if (value != null && value !== '') {
      const parsedValue = typeof value === 'string' 
        ? parseFloat(value.replace(/,/g, '')) 
        : typeof value === 'number' 
          ? value 
          : null;
      
      if (parsedValue != null && !isNaN(parsedValue)) {
        // Always use the provided value when it exists
        return String(parsedValue);
      }
    }
    
    // Return empty string if value is null/undefined/empty
    // No PropertyAssessment default - defaults are handled by parent component
    return value ?? '';
  }, [value]);
  const debouncedValidate = useRef(
    debounce((inputValue, currentIsAVMode) => {
      if (!enableValidation || inputValue == null || inputValue === '') return;
      const { adjustedValue, wasAdjusted } = validateAndAdjustOfferValue(
        inputValue,
        row,
        currentIsAVMode,
        true
      );

      if (!wasAdjusted) return;

      const formattedValue = adjustedValue.toLocaleString();
      onValueChange({
        floatValue: adjustedValue,
        formattedValue,
        value: String(adjustedValue),
      });
    }, debounceDelay)
  ).current;

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);

  // Enhanced onValueChange handler
  const handleValueChange = useCallback((vals) => {
    // Call the original onValueChange
    onValueChange(vals);
    
    // Trigger debounced validation if enabled
    if (enableValidation && vals.floatValue != null) {
      debouncedValidate(vals.floatValue, isAVMode);
    }
  }, [onValueChange, enableValidation, debouncedValidate, isAVMode]);

  const adaptedCustomInput = useMemo(
    () => (customInput ? createCustomInputAdapter(customInput) : undefined),
    [customInput],
  );

  return (
    <NumericFormat
      value={displayValue}
      onValueChange={handleValueChange}
      customInput={adaptedCustomInput}
      {...otherProps}
      {...inputProps}
    />
  );
};

export default NumericInputWithValidation;